/**
 * Example project for the empty state and demos. Invented plant, invented numbers — not taken from
 * the standard's examples (docs/standard-content-policy.md). Substance values are typical published
 * figures for illustration only; real work uses the client's SDS.
 */
import { DEFAULT_SETTINGS, type ProjectDoc } from '../model/types';

const BAR = 1e5;
const MM2 = 1e-6;
const ATM = 101_325;
const EXAMPLE = 'Example value for illustration. Use the client SDS.';

export function riversideExample(): ProjectDoc {
  return {
    schemaVersion: 1,
    meta: { name: 'Riverside Solvent Store', client: 'Example client', site: 'Riverside works', reference: 'EX-HAC-001', notes: 'Example project with invented data.' },
    settings: { ...DEFAULT_SETTINGS },
    substances: [
      {
        id: 'toluene', name: 'Toluene', casNumber: '108-88-3', state: 'liquid', molarMassKgPerKmol: 92.14,
        lflVolFrac: 0.011, uflVolFrac: 0.071, aitC: 480, flashPointC: 4, boilingPointC: 111,
        relativeVapourDensity: 3.1, liquidDensityKgM3: 867, vapourPressure: { pa: 2_900, atTemperatureK: 293.15 },
        gasGroup: 'IIA', dataSources: { all: EXAMPLE },
      },
      {
        id: 'acetone', name: 'Acetone', casNumber: '67-64-1', state: 'liquid', molarMassKgPerKmol: 58.08,
        lflVolFrac: 0.025, uflVolFrac: 0.13, aitC: 465, flashPointC: -18, boilingPointC: 56,
        relativeVapourDensity: 2.0, liquidDensityKgM3: 784, vapourPressure: { pa: 24_600, atTemperatureK: 293.15 },
        gasGroup: 'IIA', dataSources: { all: EXAMPLE },
      },
      {
        id: 'natgas', name: 'Natural gas', state: 'gas', molarMassKgPerKmol: 16.8,
        lflVolFrac: 0.044, uflVolFrac: 0.17, aitC: 537, relativeVapourDensity: 0.58, gamma: 1.31,
        gasGroup: 'IIA', dataSources: { all: EXAMPLE },
      },
    ],
    areas: [
      {
        id: 'pump-house', name: 'Pump house', kind: 'indoor',
        box: { center: [0, 0, 2], size: [10, 7, 4], yawRad: 0 },
        ventilation: { kind: 'indoor', type: 'artificial-general', airFlowM3PerS: 2.0, airFlowBasis: 'Extract fan EF-1 datasheet, 7 200 m³/h', flowDirection: [1, 0, 0], velocityReductionFactor: 1, inefficiencyF: 2 },
        availability: { level: 'good', basis: 'Duty/standby fans with automatic changeover' },
        summation: { maxSimultaneousPrimary: 'all' },
      },
      {
        id: 'tank-bund', name: 'Tank bund', kind: 'outdoor',
        box: { center: [19, 0, 0.6], size: [16, 13, 1.2], yawRad: 0 },
        ventilation: { kind: 'outdoor', velocityMPerS: 0.3, velocityBasis: 'Site wind data, 95th percentile, halved for heavy vapour (C.3.4)', obstructed: false },
        availability: { level: 'good' },
      },
      {
        id: 'gas-skid', name: 'Gas regulating skid', kind: 'outdoor',
        box: { center: [0, -13, 1], size: [6, 4, 2], yawRad: 0 },
        ventilation: { kind: 'outdoor', velocityMPerS: 1.0, velocityBasis: 'Open, elevated, unobstructed', obstructed: false },
        availability: { level: 'good' },
      },
      {
        id: 'drum-store', name: 'Drum store', kind: 'indoor',
        box: { center: [0, 11, 1.75], size: [7, 5, 3.5], yawRad: 0 },
        ventilation: { kind: 'indoor', type: 'natural', airFlowM3PerS: 0.35, airFlowBasis: 'Louvres, wind-driven, calm conditions', flowDirection: [0, 1, 0], velocityReductionFactor: 1, inefficiencyF: 3 },
        availability: { level: 'fair', basis: 'Natural ventilation, worst case' },
        summation: { maxSimultaneousPrimary: 'all' },
      },
    ],
    sources: [
      {
        id: 'sr-101', tag: 'SR-101', description: 'Pump seal, P-101 toluene transfer', areaId: 'pump-house', substanceId: 'toluene',
        position: [-2, -1, 0.6], grade: 'secondary', gradeBasis: 'Mechanical seal, release not expected in normal operation', releaseForm: 'heavy',
        release: { model: 'liquid', pressureDiffPa: 8 * BAR, holeAreaM2: 5 * MM2, cd: 0.75, vapourFraction: 0.03, vapourFractionBasis: 'Company guidance for non-flashing liquids' },
        judgements: { 'indoor-medium-extent': 'local' },
      },
      {
        id: 'sr-102', tag: 'SR-102', description: 'Discharge flange, P-101', areaId: 'pump-house', substanceId: 'toluene',
        position: [1.5, -1, 1.2], grade: 'secondary', releaseForm: 'heavy',
        release: { model: 'liquid', pressureDiffPa: 8 * BAR, holeAreaM2: 1 * MM2, cd: 0.75, vapourFraction: 0.03, vapourFractionBasis: 'Company guidance for non-flashing liquids' },
        judgements: { 'indoor-medium-extent': 'local' },
      },
      {
        id: 'sr-103', tag: 'SR-103', description: 'Sample point, toluene line', areaId: 'pump-house', substanceId: 'toluene',
        position: [3.5, 2, 1.0], grade: 'primary', gradeBasis: 'Sampled each shift', releaseForm: 'diffusive',
        release: { model: 'manual', massRateKgPerS: 2e-5, reference: 'Sampling procedure SP-4, 50 ml per sample' },
        judgements: { 'indoor-medium-extent': 'local' },
      },
      {
        id: 'sr-201', tag: 'SR-201', description: 'Breather vent, tank T-201 (acetone)', areaId: 'tank-bund', substanceId: 'acetone',
        position: [15, -2, 8.3], grade: 'primary', gradeBasis: 'Vents on every tank filling', releaseForm: 'diffusive',
        release: { model: 'manual', massRateKgPerS: 1.2e-2, reference: 'Vent sizing calculation VC-201, filling at 40 m³/h' },
      },
      {
        id: 'sr-202', tag: 'SR-202', description: 'Transfer pump seal, P-201', areaId: 'tank-bund', substanceId: 'acetone',
        position: [23, -4, 0.5], grade: 'secondary', releaseForm: 'heavy',
        release: { model: 'liquid', pressureDiffPa: 3 * BAR, holeAreaM2: 2.5 * MM2, cd: 0.75, vapourFraction: 0.05, vapourFractionBasis: 'Volatile liquid; 5 % assumed' },
      },
      {
        id: 'sr-203', tag: 'SR-203', description: 'Spill in bund sump', areaId: 'tank-bund', substanceId: 'acetone',
        position: [24.5, 3.5, 0], grade: 'secondary', releaseForm: 'heavy',
        release: { model: 'pool', areaM2: 1.5, liquidTemperatureK: 293.15 },
      },
      {
        id: 'sr-301', tag: 'SR-301', description: 'Regulator flange, 4 barg', areaId: 'gas-skid', substanceId: 'natgas',
        position: [-1, -13, 1.2], grade: 'secondary', releaseForm: 'diffusive',
        release: { model: 'gas', pressurePaAbs: 4 * BAR + ATM, temperatureK: 288.15, holeAreaM2: 0.25 * MM2, cd: 0.75, z: 1 },
      },
      {
        id: 'sr-401', tag: 'SR-401', description: 'Drum decanting point', areaId: 'drum-store', substanceId: 'toluene',
        position: [-1.5, 11, 0.9], grade: 'primary', gradeBasis: 'Daily decanting', releaseForm: 'heavy',
        release: { model: 'manual', massRateKgPerS: 4e-4, reference: 'Decanting at 20 l/min, displaced saturated vapour' },
        judgements: { 'indoor-medium-extent': 'local' },
      },
    ],
    equipment: [
      { id: 'm-101', tag: 'M-101', description: 'Pump motor, P-101', areaId: 'pump-house', box: { center: [-2.9, -1, 0.5], size: [0.9, 0.5, 0.5], yawRad: 0 }, ex: { kind: 'ex', epl: 'Gb', gasGroup: 'IIB', tClass: 'T3', marking: 'Ex db IIB T3 Gb' } },
      { id: 'jb-12', tag: 'JB-12', description: 'Junction box, lighting circuit', areaId: 'pump-house', box: { center: [-0.5, -3.3, 1.2], size: [0.3, 0.15, 0.3], yawRad: 0 }, ex: { kind: 'non-ex' } },
      { id: 'l-1', tag: 'L-1', description: 'Light fitting', areaId: 'pump-house', box: { center: [-2, 0, 3.8], size: [1.2, 0.2, 0.1], yawRad: 0 }, ex: { kind: 'ex', epl: 'Gb', gasGroup: 'IIC', tClass: 'T4', marking: 'Ex eb mb IIC T4 Gb' } },
      { id: 'pt-102', tag: 'PT-102', description: 'Pressure transmitter, P-101 discharge', areaId: 'pump-house', box: { center: [1.5, -0.6, 1.4], size: [0.15, 0.15, 0.25], yawRad: 0 }, ex: { kind: 'ex', epl: 'Ga', gasGroup: 'IIC', tClass: 'T4', marking: 'Ex ia IIC T4 Ga' } },
      { id: 'cp-1', tag: 'CP-1', description: 'Control panel', areaId: 'pump-house', box: { center: [4.4, -3.1, 1.0], size: [1.0, 0.4, 2.0], yawRad: 0 }, ex: { kind: 'non-ex' } },
      { id: 'm-201', tag: 'M-201', description: 'Pump motor, P-201', areaId: 'tank-bund', box: { center: [24, -4, 0.45], size: [0.8, 0.45, 0.45], yawRad: 0 }, ex: { kind: 'ex', epl: 'Gc', gasGroup: 'IIA', tClass: 'T3', marking: 'Ex ec IIA T3 Gc' } },
      { id: 'lt-201', tag: 'LT-201', description: 'Level transmitter, T-201', areaId: 'tank-bund', box: { center: [15.6, -2.4, 8.2], size: [0.2, 0.2, 0.3], yawRad: 0 }, ex: { kind: 'ex', epl: 'Gb', gasGroup: 'IIB', tClass: 'T4', marking: 'Ex db IIB T4 Gb' } },
      { id: 'pt-301', tag: 'PT-301', description: 'Pressure transmitter, regulator outlet', areaId: 'gas-skid', box: { center: [-0.4, -13, 1.3], size: [0.15, 0.15, 0.25], yawRad: 0 }, ex: { kind: 'ex', epl: 'Gb', gasGroup: 'IIA', tClass: 'T4', marking: 'Ex db IIA T4 Gb' } },
      { id: 'f-1', tag: 'F-1', description: 'Wall fan, drum store', areaId: 'drum-store', box: { center: [3.3, 11, 2.6], size: [0.3, 0.8, 0.8], yawRad: 0 }, ex: { kind: 'ex', epl: 'Gc', gasGroup: 'IIB', tClass: 'T3', marking: 'Ex ec IIB T3 Gc' } },
      { id: 'ef-401', tag: 'EF-401', description: 'Local extract fan motor, decanting point', areaId: 'drum-store', box: { center: [-0.7, 11.5, 1.3], size: [0.4, 0.4, 0.4], yawRad: 0 }, ex: { kind: 'ex', epl: 'Gc', gasGroup: 'IIA', tClass: 'T3', marking: 'Ex ec IIA T3 Gc' } },
    ],
    objects: [
      { id: 'p-101', name: 'P-101 pump skid', kind: 'box', box: { center: [-0.8, -1, 0.35], size: [3.2, 1.2, 0.7], yawRad: 0 } },
      { id: 't-201', name: 'Tank T-201', kind: 'cylinder', base: [15, -2, 1.2], radiusM: 2.6, heightM: 7 },
      { id: 't-202', name: 'Tank T-202', kind: 'cylinder', base: [15, 3.5, 1.2], radiusM: 2.6, heightM: 7 },
      { id: 'p-201', name: 'P-201 pump', kind: 'box', box: { center: [23.2, -4, 0.3], size: [1.6, 0.8, 0.6], yawRad: 0 } },
      { id: 'sump', name: 'Bund sump', kind: 'box', box: { center: [24.5, 3.5, 0.05], size: [1.4, 1.4, 0.1], yawRad: 0 } },
      { id: 'skid-301', name: 'Regulator skid', kind: 'box', box: { center: [0, -13, 0.4], size: [4, 1.6, 0.8], yawRad: 0 } },
      { id: 'drums', name: 'Drum racks', kind: 'box', box: { center: [-1.5, 12.2, 0.6], size: [3, 1.2, 1.2], yawRad: 0 } },
    ],
  };
}
